import { StateCreator } from 'zustand'
import { mercuryApi } from '$api'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { SharedSlice } from '../../../../../slice'
import { PayRunsSlice } from '../slice'
import { PayrollSystem } from '$constants'
import { PayRun, transformPayRunOnGet } from '$models'

interface EditPayRunState {
  payRun: PayRun | null
}

export interface EditPayRunSlice extends EditPayRunState {
  /**
   * Reset PayRun Slice
   * @returns a void
   */
  resetEditPayRunSlice: () => void

  /**
   * Get PayRun By id
   * @param id PayRun id
   * @param payrollSystem Payroll System that the PayRun belongs to
   * @returns a Promise<void>
   */
  getPayRun: (id: string, payrollSystem: PayrollSystem) => Promise<void>

  /**
   * Update PayRun
   * @param payRun PayRun to update
   * @param payrollSystem Payroll System that the PayRun belongs to
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns a Promise<void>
   */
  updatePayRun: (
    payRun: PayRun,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Sync employee/s wages
   * @param payRunId is the payRun id
   * @param employeeIds are the ids of the employees that needs to be synced
   * @param payrollSystem Payroll System that the PayRun belongs to
   * @param successMessage Success Message to display if the sync was successful
   * @param errorMessage Error Message to display if the sync has failed
   * @returns a Promise<void>
   */
  syncWages: (
    payRunId: string,
    employeeIds: string[],
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>
}

const initialState: EditPayRunState = {
  payRun: null, //TODO: remove null
}

const createEditPayRunSlice: StateCreator<
  PayRunsSlice & EditPayRunSlice & SharedSlice,
  [],
  [],
  EditPayRunSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditPayRunSlice: () => set(initialState),

  // State API calls
  getPayRun: async (
    id: string,
    payrollSystem: PayrollSystem
  ): Promise<void> => {
    return mercuryApi
      .get<PayRun>(`/payroll/pay-runs/${id}?system=${payrollSystem}`)
      .then((result) => set(() => ({ payRun: transformPayRunOnGet(result) })))
      .catch((error) => {
        set(() => ({
          payRun: null,
        }))
        exceptionMessageHandler(error, 'Failed to retrieve payrun')
      })
  },

  updatePayRun: async (
    payrun: PayRun,
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const updatedPayRun: PayRun = await mercuryApi.update(
        `/payroll/pay-runs/${payrun.id}?system=${payrollSystem}`,
        payrun
      )
      if (!updatedPayRun) {
        logger.error(`${errorMessage} - updatedPayRun doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      set(() => ({
        payRun: transformPayRunOnGet(updatedPayRun),
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  syncWages: async (
    payRunId: string,
    employeeIds: string[],
    payrollSystem: PayrollSystem,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      const updatedPayRun: PayRun = await mercuryApi.add(
        `/payroll/pay-runs/sync?system=${payrollSystem}`,
        {
          payRunId,
          employeeIds,
        }
      )
      if (!updatedPayRun) {
        logger.error(`${errorMessage} - updatedPayRun doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      set(() => ({
        payRun: transformPayRunOnGet(updatedPayRun),
      }))
      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },
})

export default createEditPayRunSlice
